'use client'

import { disableQuery, useMutation, useQuery } from '@connectrpc/connect-query'
import { UserProfile } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/user_profile_pb'
import {
  checkIppsPaymentStatusPublic,
  createIppsQrPaymentPublic,
} from '@fingertip/creator-proto/gen/fingertip/creator/store_payment/v1/store_payment-StorePaymentService_connectquery'
import { zodResolver } from '@hookform/resolvers/zod'
import { StripeElementsOptionsMode } from '@stripe/stripe-js'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import {
  createPaymentDetailsFormSchema,
  PaymentDetailsFormSchema,
} from '@/components/checkout/types'
import { FormErrors } from '@/components/fields/form-errors'
import { QrHtmlInnerElement } from '@/components/qr/qr-html-inner-element'
import { useToken } from '@/lib/hooks/use-token'

import { CheckboxField } from '../fields/checkbox-field'
import { InputField } from '../fields/input-field'
import { Button } from '../ui/button'

type Props = {
  siteSlug: string
  defaultValues: PaymentDetailsFormSchema
  currentUser?: UserProfile
  options: StripeElementsOptionsMode
  getPaymentParams: () => {
    clientSecret: string | undefined
    returnUrl: string
  }
  onPaymentInitiated?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
  onPaymentUpdated?: () => Promise<void>
  onPaymentConfirmed?: ({
    input,
  }: {
    input: PaymentDetailsFormSchema
  }) => Promise<void>
}

export const IppsCheckout = ({
  siteSlug,
  defaultValues,
  onPaymentInitiated,
  onPaymentConfirmed,
}: Props) => {
  const { callOptions } = useToken()
  const [qrCodeRaw, setQrCodeRaw] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [clientTransactionId, setClientTransactionId] = useState<string | null>(
    null,
  )

  // QR Payment mutation
  const mutationRequestQR = useMutation(createIppsQrPaymentPublic, {
    callOptions,
    onError: (error) => {
      toast.error(error.message)
    },
  })

  // Status check query
  const { data: statusData } = useQuery(
    checkIppsPaymentStatusPublic,
    !clientTransactionId
      ? disableQuery
      : {
          clientTransactionId,
          siteSlug,
        },
    {
      callOptions,
      refetchInterval: 3000, // Poll every 3 seconds
      enabled: !!clientTransactionId,
    },
  )

  const form = useForm<PaymentDetailsFormSchema>({
    resolver: zodResolver(
      createPaymentDetailsFormSchema({ requiresShipping: false }),
    ),
    mode: 'all',
    defaultValues,
  })

  useEffect(() => {
    if (!statusData) {
      return
    }

    if (statusData.code === 11) {
      // Payment successful
      onPaymentConfirmed?.({ input: form.getValues() })
    } else if (statusData.code === 12) {
      // Payment rejected - show error once and reset state
      toast.error('Payment was rejected')
      setClientTransactionId(null) // Stop the polling
      setQrCodeRaw(null) // Clear the QR code
    }
  }, [form, onPaymentConfirmed, statusData])

  const submitHandler = useCallback(
    async (input: PaymentDetailsFormSchema) => {
      try {
        setIsLoading(true)

        if (onPaymentInitiated) {
          await onPaymentInitiated({ input })
        }

        // Generate unique transaction ID
        const newClientTransactionId = `ipps-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        setClientTransactionId(newClientTransactionId)

        const response = await mutationRequestQR.mutateAsync({
          siteSlug,
          clientTransactionId: newClientTransactionId,
        })

        setQrCodeRaw(response.qrRaw)
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        toast.error('Failed to initiate payment')
      } finally {
        setIsLoading(false)
      }
    },
    [onPaymentInitiated, mutationRequestQR, siteSlug],
  )

  return (
    <form onSubmit={form.handleSubmit(submitHandler)}>
      <div className="mb-4 font-semibold">Enter payment details</div>

      <div className="mb-4">
        <InputField
          control={form.control}
          type="email"
          name="contact.email"
          placeholder="E.g. you@email.com"
        />

        <CheckboxField
          name="contact.emailOptin"
          control={form.control}
          label="Email me about new products and offers"
        />
      </div>

      <FormErrors errors={form.formState.errors} />

      {qrCodeRaw ? (
        <div className="mt-6 text-center">
          <h2 className="h5 mb-4">Scan QR code to pay</h2>
          <div className="flex w-full items-start justify-center rounded-lg bg-white p-4">
            <QrHtmlInnerElement
              data={qrCodeRaw}
              theme={{
                id: 'custom',
                key: 'Custom',
                logoColor: 'currentColor',
                foregroundColor: 'currentColor',
                backgroundColor: 'currentColor',
                borderColor: 'currentColor',
              }}
              hasLogo={false}
              className="aspect-square w-full"
              style={{
                maxHeight: 200,
                maxWidth: 200,
              }}
              correction="H"
            />
          </div>

          <p className="mt-4 text-sm text-muted-foreground">
            Please scan this QR code with your banking app to complete the
            payment
          </p>
        </div>
      ) : (
        <Button
          type="submit"
          className="w-full"
          loading={isLoading || mutationRequestQR.isPending}
        >
          Generate QR code
        </Button>
      )}
    </form>
  )
}
